import { logger } from '@/lib/logger';

import { getSessionReplayId } from '@/utils/amplitude';

declare global {
  interface Window {
    Pylon: ((...args: unknown[]) => void) & { q: unknown[]; e: (e: unknown) => void };
    __matchaPylonThemeSyncCleanup?: () => void;
    pylon: {
      chat_settings: {
        app_id: string;
        email: string;
        name: string;
      };
    };
  }
}

type PylonTheme = 'light' | 'dark';
type PylonWalletContext = { evmAddress?: string; solanaAddress?: string };

const PYLON_APP_ID = 'e586362e-bdd0-4f5d-a275-2879d9f2442a';
const PYLON_WIDGET_SRC = `https://widget.usepylon.com/widget/${PYLON_APP_ID}`;
const PYLON_THEME_MEDIA_QUERY = '(prefers-color-scheme: dark)';

// Pylon issue custom field slugs (must match the slugs configured in Pylon).
const PYLON_FIELD_EVM_WALLET = 'connected_evm_wallet';
const PYLON_FIELD_SOLANA_WALLET = 'connected_solana_wallet';
const PYLON_FIELD_SESSION_REPLAY = 'amplitude_session_replay';

// Base for Amplitude session replay deep links (0x org, prod project, US region).
// The `sessionReplayId` value (`<deviceId>/<sessionId>`) keeps its literal slash,
// so callers must append it without URL-encoding.
const AMPLITUDE_SESSION_REPLAY_URL_BASE =
  'https://app.amplitude.com/analytics/0x/session-replay/project/427066/search/replay?sessionReplayId=';

// Latest connected wallet addresses, cached so showPylonChat (a bare click
// handler with no React context) can re-push them when the user opens the chat.
let connectedWalletContext: PylonWalletContext = {};

let initializeSessionPromise: Promise<boolean> | undefined;

function cleanupPylonThemeSync() {
  window.__matchaPylonThemeSyncCleanup?.();
  window.__matchaPylonThemeSyncCleanup = undefined;
}

function getPylonTheme(): PylonTheme {
  const theme = document.documentElement.dataset['theme'] ?? document.body.dataset['theme'];

  if (theme === 'dark') {
    return 'dark';
  }

  if (theme === 'light') {
    return 'light';
  }

  return window.matchMedia(PYLON_THEME_MEDIA_QUERY).matches ? 'dark' : 'light';
}

function syncPylonTheme() {
  window.Pylon('setTheme', getPylonTheme());
}

function observePylonThemeChanges() {
  const observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.attributeName === 'data-theme')) {
      syncPylonTheme();
    }
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });

  const mediaQuery = window.matchMedia(PYLON_THEME_MEDIA_QUERY);
  const handleThemeChange = () => {
    syncPylonTheme();
  };
  const usesEventListener = typeof mediaQuery.addEventListener === 'function';

  if (usesEventListener) {
    mediaQuery.addEventListener('change', handleThemeChange);
  } else if (typeof mediaQuery.addListener === 'function') {
    mediaQuery.addListener(handleThemeChange);
  }

  return () => {
    observer.disconnect();

    if (usesEventListener) {
      mediaQuery.removeEventListener('change', handleThemeChange);
    } else if (typeof mediaQuery.removeListener === 'function') {
      mediaQuery.removeListener(handleThemeChange);
    }
  };
}

export function initializePylon() {
  (function () {
    const e = window;
    const t = document;
    const n = function (...args: unknown[]) {
      n.e(args);
    };
    n.q = [] as unknown[];
    n.e = function (e: unknown) {
      n.q.push(e);
    };
    e.Pylon = n;
    const r = function () {
      if (t.querySelector(`script[src="${PYLON_WIDGET_SRC}"]`)) {
        return;
      }
      const e = t.createElement('script');
      e.setAttribute('type', 'text/javascript');
      e.setAttribute('async', 'true');
      e.setAttribute('src', PYLON_WIDGET_SRC);
      const n = t.getElementsByTagName('script')[0];
      n?.parentNode?.insertBefore(e, n);
    };
    if (t.readyState === 'complete') {
      r();
    } else if (e.addEventListener) {
      e.addEventListener('load', r, false);
    }
  })();

  syncPylonTheme();
  cleanupPylonThemeSync();

  const cleanup = observePylonThemeChanges();
  window.__matchaPylonThemeSyncCleanup = cleanup;

  return () => {
    if (window.__matchaPylonThemeSyncCleanup === cleanup) {
      cleanupPylonThemeSync();
    }
  };
}

/**
 * Attaches debugging context (connected wallets and, when available, an Amplitude
 * session replay link) to new Pylon chat issues via `setNewIssueCustomFields`.
 *
 * Wallet fields are always sent — an empty string clears a stale address from a
 * previous connection in this session. The session replay field is only included
 * when the current session is being recorded; the id is read fresh on each push
 * because it may only become available partway through a session.
 */
function pushPylonSupportContext() {
  if (typeof window === 'undefined' || typeof window.Pylon !== 'function') {
    return;
  }

  const replayId = getSessionReplayId();

  window.Pylon('setNewIssueCustomFields', {
    [PYLON_FIELD_EVM_WALLET]: connectedWalletContext.evmAddress ?? '',
    [PYLON_FIELD_SOLANA_WALLET]: connectedWalletContext.solanaAddress ?? '',
    ...(replayId
      ? { [PYLON_FIELD_SESSION_REPLAY]: `${AMPLITUDE_SESSION_REPLAY_URL_BASE}${replayId}` }
      : {}),
  });
}

async function initializePylonSession() {
  return new Promise<boolean>((resolve, reject) => {
    let timeout: ReturnType<typeof setTimeout> | undefined = undefined;
    let initializationInterval: ReturnType<typeof setInterval> | undefined = undefined;

    window.pylon = {
      chat_settings: { app_id: PYLON_APP_ID, email: 'user@matcha.xyz', name: 'User' },
    };

    const clearTimers = () => {
      clearTimeout(timeout);
      clearInterval(initializationInterval);
    };

    timeout = setTimeout(() => {
      initializeSessionPromise = undefined;
      clearTimers();
      reject(new Error('Pylon widget timeout'));
    }, 10_000);

    initializationInterval = setInterval(() => {
      const pylonChatElement = document.getElementById('pylon-chat-bubble');

      if (pylonChatElement) {
        // Hide chat bubble by default
        window.Pylon('hideChatBubble');
        clearTimers();
        resolve(true);
      }
    }, 500);
  });
}

// Make sure to call initializePylon before initializing a Pylon session
export async function ensurePylonSession() {
  if (initializeSessionPromise) {
    return initializeSessionPromise;
  }

  initializeSessionPromise = initializePylonSession();
  return initializeSessionPromise;
}

/**
 * Updates the cached connected wallet addresses. Call whenever the connected
 * wallets change; showPylonChat pushes the cached context to Pylon when needed.
 */
export function setPylonWalletContext(context: PylonWalletContext) {
  connectedWalletContext = context;
}

export async function showPylonChat() {
  try {
    await ensurePylonSession();
  } catch (error) {
    logger.warn('Initialize pylon session error', error);
    return;
  }

  syncPylonTheme();

  // Re-push so the issue carries the freshest session replay id and wallet state
  // at the moment the user can create an issue.
  pushPylonSupportContext();

  window.Pylon('show');
  window.Pylon('onHide', () => window.Pylon('hideChatBubble'));
}
